import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertCircle, Clock, CheckCircle2, Filter, RefreshCw,
  Building2, HardHat, Wrench, ChevronRight, User, Bot,
  ArrowUpRight, XCircle, MessageSquare
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';

const BRONZE = '#C9A961';

const WaitingOnMePage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDivision, setSelectedDivision] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    fetchItems();
  }, [selectedDivision, selectedPriority]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      let url = '/command-center/waiting-on-me?status=open';
      if (selectedDivision !== 'all') {
        url += `&division_id=${selectedDivision}`;
      }
      if (selectedPriority !== 'all') {
        url += `&priority=${selectedPriority}`;
      }
      const response = await api.get(url);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to load queue');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedItem) return;
    
    try {
      setResolving(true);
      await api.patch(`/command-center/waiting-on-me/${selectedItem.id}`, {
        status: 'resolved',
        resolution_notes: resolutionNotes
      });
      toast.success('Item resolved');
      setShowResolveDialog(false);
      setSelectedItem(null);
      setResolutionNotes('');
      fetchItems();
    } catch (error) {
      console.error('Error resolving item:', error);
      toast.error('Failed to resolve item');
    } finally {
      setResolving(false);
    }
  };

  const getDivisionIcon = (divisionId) => {
    switch (divisionId) {
      case 'government': return Building2;
      case 'gc': return HardHat;
      case 'fm': return Wrench;
      default: return AlertCircle;
    }
  };

  const getDivisionColor = (divisionId) => {
    switch (divisionId) {
      case 'government': return 'bg-blue-500/20 text-blue-400';
      case 'gc': return 'bg-orange-500/20 text-orange-400';
      case 'fm': return 'bg-green-500/20 text-green-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-black';
      case 'low': return 'bg-gray-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getRequestTypeLabel = (type) => {
    const labels = {
      approval_needed: 'Approval Needed',
      missing_info: 'Missing Info',
      pricing_decision: 'Pricing Decision',
      compliance_issue: 'Compliance Issue',
      urgent_deadline: 'Urgent Deadline',
      review_required: 'Review Required',
      escalation: 'Escalation'
    };
    return labels[type] || type;
  };

  const groupedItems = items.reduce((acc, item) => {
    const division = item.division_id;
    if (!acc[division]) acc[division] = [];
    acc[division].push(item);
    return acc;
  }, {});

  const urgentCount = items.filter(i => i.priority === 'urgent').length;
  const highCount = items.filter(i => i.priority === 'high').length;

  return (
    <div className="min-h-screen p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src="/williams-logo-transparent.png" alt="Logo" className="h-16" />
          <div>
            <h1 className="text-4xl font-bold font-serif flex items-center gap-3">
              <AlertCircle className="h-10 w-10 text-red-400" />
              <span className="text-white">Waiting</span>{' '}
              <span className="italic" style={{ color: BRONZE }}>On Me</span>
            </h1>
            <p className="text-gray-400 text-lg">Master Control Queue - Human Decisions Required</p>
          </div>
        </div>
        
        <div className="flex gap-3">
          <Button variant="outline" className="border-gray-700" onClick={fetchItems}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Alert Banner */}
      {urgentCount > 0 && (
        <div className="p-4 rounded-lg bg-red-500/20 border border-red-500/50 flex items-center gap-3">
          <AlertCircle className="h-6 w-6 text-red-400" />
          <div>
            <p className="font-semibold text-red-400">
              {urgentCount} urgent item{urgentCount !== 1 ? 's' : ''} require immediate attention
            </p>
            <p className="text-sm text-red-300/70">
              {highCount > 0 && `Plus ${highCount} high priority items`}
            </p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-black/50 border-gray-800">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-gray-800">
              <AlertCircle className="h-6 w-6 text-gray-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{items.length}</p>
              <p className="text-sm text-gray-400">Total Open</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-black/50 border-gray-800">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-red-500/20">
              <AlertCircle className="h-6 w-6 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-400">{urgentCount}</p>
              <p className="text-sm text-gray-400">Urgent</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-black/50 border-gray-800">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-orange-500/20">
              <Clock className="h-6 w-6 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-400">{highCount}</p>
              <p className="text-sm text-gray-400">High Priority</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-black/50 border-gray-800">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg" style={{ backgroundColor: `${BRONZE}20` }}>
              <CheckCircle2 className="h-6 w-6" style={{ color: BRONZE }} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: BRONZE }}>
                {Object.keys(groupedItems).length}
              </p>
              <p className="text-sm text-gray-400">Divisions</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={selectedDivision} onValueChange={setSelectedDivision}>
          <SelectTrigger className="w-48 bg-black/50 border-gray-700">
            <SelectValue placeholder="All Divisions" />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-gray-700">
            <SelectItem value="all">All Divisions</SelectItem>
            <SelectItem value="government">Government</SelectItem>
            <SelectItem value="gc">General Contracting</SelectItem>
            <SelectItem value="fm">Facility Maintenance</SelectItem>
          </SelectContent>
        </Select>

        <Select value={selectedPriority} onValueChange={setSelectedPriority}>
          <SelectTrigger className="w-48 bg-black/50 border-gray-700">
            <SelectValue placeholder="All Priorities" />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-gray-700">
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Items List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin" style={{ color: BRONZE }} />
        </div>
      ) : items.length === 0 ? (
        <Card className="bg-black/50 border-gray-800">
          <CardContent className="p-12 text-center">
            <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-green-500" />
            <h3 className="text-xl font-semibold text-white mb-2">All Clear!</h3>
            <p className="text-gray-400">No items waiting for your action</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedItems).map(([divisionId, divisionItems]) => {
            const DivIcon = getDivisionIcon(divisionId);
            return (
              <Card key={divisionId} className="bg-black/50 border-gray-800">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${getDivisionColor(divisionId)}`}>
                      <DivIcon className="h-5 w-5" />
                    </div>
                    <span className="text-white capitalize">{divisionId}</span>
                    <Badge variant="outline" className="ml-auto">
                      {divisionItems.length} items
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {divisionItems.map(item => (
                      <div 
                        key={item.id}
                        className={`p-4 rounded-lg border transition-all cursor-pointer hover:border-gray-600 ${
                          item.priority === 'urgent' 
                            ? 'bg-red-500/10 border-red-500/30' 
                            : 'bg-gray-900/50 border-gray-800'
                        }`}
                        onClick={() => {
                          setSelectedItem(item);
                          setShowResolveDialog(true);
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={getPriorityColor(item.priority)}>
                                {item.priority}
                              </Badge>
                              <Badge variant="outline" className="text-gray-400">
                                {getRequestTypeLabel(item.request_type)}
                              </Badge>
                              {item.created_by_agent_id && (
                                <Badge variant="outline" className="text-blue-400 border-blue-400/50">
                                  <Bot className="h-3 w-3 mr-1" />
                                  AI Agent
                                </Badge>
                              )}
                            </div>
                            <p className="text-white font-medium">{item.summary}</p>
                            {item.recommended_action && (
                              <p className="text-sm text-gray-400 mt-1">
                                <span className="text-gray-500">Recommended:</span> {item.recommended_action}
                              </p>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            {item.deadline && (
                              <p className="text-sm text-gray-500">
                                <Clock className="h-3 w-3 inline mr-1" />
                                {new Date(item.deadline).toLocaleDateString()}
                              </p>
                            )}
                            <ChevronRight className="h-5 w-5 text-gray-600 mt-2" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Resolve Dialog */}
      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ color: BRONZE }}>Resolve Item</DialogTitle>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-black/50 border border-gray-800">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={getPriorityColor(selectedItem.priority)}>
                    {selectedItem.priority}
                  </Badge>
                  <Badge variant="outline" className="text-gray-400">
                    {getRequestTypeLabel(selectedItem.request_type)}
                  </Badge>
                </div>
                <p className="text-white">{selectedItem.summary}</p>
                {selectedItem.recommended_action && (
                  <p className="text-sm text-gray-400 mt-2">
                    <span className="font-medium">Recommended:</span> {selectedItem.recommended_action}
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-2 block">Resolution Notes</label>
                <Textarea
                  placeholder="Enter resolution notes..."
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  className="bg-black/50 border-gray-700 min-h-24"
                />
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1 border-gray-700"
                  onClick={() => {
                    setShowResolveDialog(false);
                    setSelectedItem(null);
                    setResolutionNotes('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => navigate(`/${selectedItem.division_id}/opportunities/${selectedItem.related_record_id}`)}
                  variant="outline"
                  className="flex-1 border-gray-700"
                >
                  <ArrowUpRight className="h-4 w-4 mr-2" />
                  View Record
                </Button>
                <Button
                  onClick={handleResolve}
                  disabled={resolving}
                  className="flex-1"
                  style={{ backgroundColor: BRONZE, color: 'black' }}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {resolving ? 'Resolving...' : 'Resolve'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WaitingOnMePage;
